import { addDays, mondayOf, parseISODate, toISODate } from './date';
import {
  ANNUAL_WEEKDAY_KEYS,
  AnnualClassEntry,
  buildAnnualCellKey,
  buildAnnualPeriodLabels,
  deriveAnnualEffectiveCell,
  isAnnualShortDayTailMemo,
  isDedicatedColor,
  annualSlotIsFixedProgram,
} from './annualTimetable';
import {
  matchesAnnualProjectProgramCell,
  type AnnualProjectProgram,
} from './annualProjectProgramsStorage';
import { timetableTextForOffDayRow, type OffDayByDate } from './annualProgressDataCache';
import { mapProgressLikeToDisplayFields } from './annualProgressFieldDisplay';
import {
  annualSlotHasTimetableProgramAssignment,
  buildScopedRowsAnnualIndex,
  eventNameFromFixedContentRow,
  normalizeFixedEventName,
  pickRepScopedContentRowForMirrorSlot,
  pickScopedContentFromAnnualIndex,
  pickScopedRecordRowFromAnnualIndex,
  resolveAnnualSlotProgressSource,
  type ScopedRowsAnnualIndex,
} from './annualProgressSlotSource';
import {
  buildGradeTrackTimetableContext,
  normalizeTimetableTextToDefaultAlias,
  resolveTrackNameFromTimetableText,
  type GradeTrackTimetableContext,
} from './trackTimetableAlias';
import type {
  AcademicTerm,
  AnnualTimetableCell,
  AnnualWeekdayKey,
  ProgramFixedContentRow,
  ProgramProgressCell,
  ProgramScopedContentRow,
  Track,
} from '../types/domain';
import type { PageSectionLayout } from '../services/sectionLayoutService';
import type { TrackProgramMode } from './trackProgramModeStorage';
import { resolveAnnualPresentationSyncMirror } from './programPresentationList';
import { isSyncTrackMirrorScope } from './trackSyncScopedContent';
import {
  resolveAnnualProgressRecordTargets,
  type AnnualProgressProgramRecordTarget,
} from './annualProgressProgramRecordTarget';

type WeekInfo = {
  weekIndex: number;
  mondayIso: string;
  datesByWeekday: string[];
};

function buildWeeksFromRange(startsOn: string, endsOn: string): WeekInfo[] {
  if (!startsOn || !endsOn) return [];
  const endDate = parseISODate(endsOn);
  const endMonday = mondayOf(endsOn);
  const endMondayIso = toISODate(endMonday);
  let cur = mondayOf(startsOn);
  const list: WeekInfo[] = [];
  let weekIndex = 0;
  while (cur.getTime() <= endDate.getTime()) {
    const mondayIso = toISODate(cur);
    const datesByWeekday: string[] = [];
    for (let d = 0; d < 6; d += 1) {
      datesByWeekday.push(toISODate(addDays(cur, d)));
    }
    list.push({ weekIndex: ++weekIndex, mondayIso, datesByWeekday });
    cur = addDays(cur, 7);
  }
  const hasEndWeek = list.some((w) => w.mondayIso === endMondayIso);
  if (!hasEndWeek) {
    const datesByWeekday: string[] = [];
    for (let d = 0; d < 6; d += 1) {
      datesByWeekday.push(toISODate(addDays(endMonday, d)));
    }
    list.push({ weekIndex: ++weekIndex, mondayIso: endMondayIso, datesByWeekday });
  }
  return list;
}

export type AnnualCrossTopicCountScope = 'term1' | 'term2' | 'annual';

export type AnnualCrossTopicDetailRow = {
  dateLabel: string;
  weekdayLabel: string;
  periodLabel: string;
  templateLabel: string;
  templateTrackLabel?: string;
  templateTimetableLabel?: string;
  unit: string;
  learning: string;
  page: string;
  assistantPage: string;
  currentPeriod: string;
  totalPeriod: string;
  material: string;
  crossTopic: string;
  crossNote: string;
  /** 편제 동기화 미러(대표가 아닌 alias) 행 — 카운팅에서 제외 가능 */
  isSyncTimetableMirror?: boolean;
  /** 연간 슬롯 키 — 프로그램 표 초안 병합용 */
  annualCellKey?: string;
  /** includeEmptyCross 모드에서만: 범교과(내용) 저장 원본(동기화 미러는 대표 행) */
  contentRecordTarget?: AnnualProgressProgramRecordTarget | null;
  mondayIso: string;
  weekdayKey: AnnualWeekdayKey;
  slotIndex: number;
  seq: number;
};

function weekdayLabelForKey(key: AnnualWeekdayKey): string {
  return ANNUAL_WEEKDAY_KEYS.find((w) => w.key === key)?.label ?? key;
}

function shortDateLabelForIso(iso: string): string {
  const d = parseISODate(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getMonth() + 1}월${d.getDate()}일`;
}

export function buildAnnualProgressLearningCrossSuffix(crossTopic: string, crossNote: string): string {
  const topic = crossTopic.trim();
  const note = crossNote.trim();
  if (topic) return `[${topic}]${note ? ` ${note}` : ''}`;
  return note;
}

export function sortAnnualCrossTopicDetailRows(rows: AnnualCrossTopicDetailRow[]): AnnualCrossTopicDetailRow[] {
  return [...rows].sort((a, b) => {
    const ka = `${a.mondayIso}|${a.weekdayKey}|${String(a.slotIndex).padStart(2, '0')}|${String(a.seq).padStart(5, '0')}`;
    const kb = `${b.mondayIso}|${b.weekdayKey}|${String(b.slotIndex).padStart(2, '0')}|${String(b.seq).padStart(5, '0')}`;
    return ka.localeCompare(kb);
  });
}

export function filterAnnualCrossTopicDetailRowsByTopic(
  rows: AnnualCrossTopicDetailRow[],
  topic: string
): AnnualCrossTopicDetailRow[] {
  const needle = topic.trim();
  if (!needle) return [];
  return sortAnnualCrossTopicDetailRows(rows.filter((row) => row.crossTopic.trim() === needle));
}

export type AnnualCrossTopicProgramPlanGroup = {
  topic: string;
  rows: AnnualCrossTopicDetailRow[];
};

export function annualCrossTopicProgramPlanTitle(scope: AnnualCrossTopicCountScope): string {
  if (scope === 'term1') return '1학기 범교과 주제 프로그램 운영 계획';
  if (scope === 'term2') return '2학기 범교과 주제 프로그램 운영 계획';
  return '연간 범교과 주제 프로그램 운영 계획';
}

export function annualCrossTopicScopeLabel(scope: AnnualCrossTopicCountScope): string {
  if (scope === 'term1') return '1학기';
  if (scope === 'term2') return '2학기';
  return '연간';
}

/** 주제 순서(topicOrder)대로 범교과 주제별 프로그램 행을 묶는다. */
export function buildAnnualCrossTopicProgramPlanGroups(
  rows: AnnualCrossTopicDetailRow[],
  topicOrder: string[]
): AnnualCrossTopicProgramPlanGroup[] {
  const sorted = sortAnnualCrossTopicDetailRows(rows);
  const byTopic = new Map<string, AnnualCrossTopicDetailRow[]>();
  for (const row of sorted) {
    const topic = row.crossTopic.trim();
    if (!topic) continue;
    const bucket = byTopic.get(topic);
    if (bucket) bucket.push(row);
    else byTopic.set(topic, [row]);
  }
  return topicOrder
    .map((topic) => {
      const groupRows = byTopic.get(topic.trim());
      if (!groupRows?.length) return null;
      return { topic: topic.trim(), rows: groupRows };
    })
    .filter((group): group is AnnualCrossTopicProgramPlanGroup => group != null);
}

export type ExtractAnnualProgressCrossTopicsParams = {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  selectedEntry: AnnualClassEntry;
  termDef: AcademicTerm;
  cellsByKey: Record<string, AnnualTimetableCell>;
  programCellsByKey: Record<string, ProgramProgressCell>;
  scopedRowsForTerm: ProgramScopedContentRow[];
  fixedContentRows: ProgramFixedContentRow[];
  holidaySet: Set<string>;
  offDayByDate: OffDayByDate;
  dedicatedColors: string[];
  baseExcludeColors: string[];
  regularColors: string[];
  annualProjectProgramsForSelectedGrade: AnnualProjectProgram[];
  trackProgramModeByTrack: Record<string, TrackProgramMode>;
  trackProgramSyncRepByTrack: Record<string, string>;
  tracks: Track[];
  gradeTrackSections: PageSectionLayout[];
  trackNameByTimetableTextByGrade: Record<string, Record<string, string>>;
  /** true면 범교과가 비어 있는 수업 행도 포함(AI 범교과 자동 구성 후보용) */
  includeEmptyCross?: boolean;
};

/** 연간진도표 테이블과 동일한 규칙으로 범교과 주제가 있는 행 수집 */
export function extractAnnualProgressCrossTopicDetailRows(
  params: ExtractAnnualProgressCrossTopicsParams
): AnnualCrossTopicDetailRow[] {
  const {
    workspaceId,
    academicYear,
    termNumber,
    selectedEntry,
    termDef,
    cellsByKey,
    programCellsByKey,
    scopedRowsForTerm,
    fixedContentRows,
    holidaySet,
    offDayByDate,
    dedicatedColors,
    baseExcludeColors,
    regularColors,
    annualProjectProgramsForSelectedGrade,
    trackProgramModeByTrack,
    trackProgramSyncRepByTrack,
    tracks,
    gradeTrackSections,
    trackNameByTimetableTextByGrade,
    includeEmptyCross = false,
  } = params;

  const visibleWeekdays = ANNUAL_WEEKDAY_KEYS.filter((w) => w.key !== 'sat') as Array<{
    key: AnnualWeekdayKey;
    label: string;
  }>;
  const weeks = buildWeeksFromRange(termDef.starts_on, termDef.ends_on);
  const periodLabels = buildAnnualPeriodLabels(selectedEntry.sectionRows);
  if (weeks.length === 0 || periodLabels.length === 0) return [];

  const scopedRowsIndex: ScopedRowsAnnualIndex = buildScopedRowsAnnualIndex(scopedRowsForTerm);
  const fixedContentByEventName = new Map<string, ProgramFixedContentRow>();
  fixedContentRows.forEach((row) => {
    const key = eventNameFromFixedContentRow(row);
    if (!key || fixedContentByEventName.has(key)) return;
    fixedContentByEventName.set(key, row);
  });
  const fixedContentBySourceAnnualKey = new Map<string, ProgramFixedContentRow>();
  fixedContentRows.forEach((row) => {
    const k = (row.source_annual_cell_key ?? '').trim();
    if (!k || fixedContentBySourceAnnualKey.has(k)) return;
    fixedContentBySourceAnnualKey.set(k, row);
  });
  const projectProgramByColor = new Map<string, AnnualProjectProgram>();
  annualProjectProgramsForSelectedGrade.forEach((program) => {
    if (program.termNumber !== termNumber) return;
    const key = program.colorHex.trim().toLowerCase();
    if (!key || projectProgramByColor.has(key)) return;
    projectProgramByColor.set(key, program);
  });

  const selectedGradeTrackTimetableContext: GradeTrackTimetableContext | null = (() => {
    const gid = (selectedEntry.gradeId ?? '').trim();
    if (!gid) return null;
    return buildGradeTrackTimetableContext(workspaceId, gid, tracks, gradeTrackSections);
  })();

  const scopedOccurrenceCountByScopeKey = new Map<string, number>();
  const out: AnnualCrossTopicDetailRow[] = [];
  let seq = 0;

  weeks.forEach((week) => {
    visibleWeekdays.forEach(({ key }, weekdayIdx) => {
      const weekdayKey = key as AnnualWeekdayKey;
      const dateIso = week.datesByWeekday[weekdayIdx] ?? '';
      for (let slotIndex = 0; slotIndex < periodLabels.length; slotIndex += 1) {
        const stored = cellsByKey[buildAnnualCellKey(selectedEntry.gradeId, week.mondayIso, weekdayKey, slotIndex)];
        const effective = deriveAnnualEffectiveCell({
          workspaceId,
          academicYear,
          termNumber,
          gradeId: selectedEntry.gradeId,
          mondayIso: week.mondayIso,
          weekdayKey,
          slotIndex,
          dateIso,
          sectionRows: selectedEntry.sectionRows,
          slotLimitByDay: selectedEntry.slotLimitByDay,
          startsOn: termDef.starts_on,
          endsOn: termDef.ends_on,
          holidaySet,
          storedCell: stored,
          dedicatedColors,
          baseExcludeColors,
        });
        const rawDisplayText = (effective.textValue ?? '').trim();
        let templateLabel = rawDisplayText
          ? trackNameByTimetableTextByGrade[selectedEntry.gradeId]?.[rawDisplayText] ?? ''
          : '';
        if (!templateLabel && rawDisplayText && selectedGradeTrackTimetableContext) {
          templateLabel = resolveTrackNameFromTimetableText(rawDisplayText, selectedGradeTrackTimetableContext);
        }
        const normalizedDisplayText =
          rawDisplayText && selectedGradeTrackTimetableContext
            ? normalizeTimetableTextToDefaultAlias(
                rawDisplayText,
                selectedGradeTrackTimetableContext,
                templateLabel
              )
            : rawDisplayText;
        const displayColor = (effective.colorHex ?? '').trim();
        const annualCellKey = buildAnnualCellKey(selectedEntry.gradeId, week.mondayIso, weekdayKey, slotIndex);
        const timetableMemoRaw = isAnnualShortDayTailMemo(effective.memo) ? '' : (effective.memo ?? '').trim();
        const programKindLabel = (() => {
          if (
            matchesAnnualProjectProgramCell(
              displayColor,
              annualProjectProgramsForSelectedGrade,
              termNumber,
              effective.isBaseExcluded
            )
          ) {
            return '프로';
          }
          if (
            annualSlotIsFixedProgram({
              colorHex: displayColor,
              regularColors,
            })
          ) {
            return '고정';
          }
          if (isDedicatedColor(displayColor, dedicatedColors)) return '전담';
          return '';
        })();
        const hasTimetableAssignment = annualSlotHasTimetableProgramAssignment(rawDisplayText, displayColor);
        const isFixedSlot = programKindLabel === '고정';
        let pc = hasTimetableAssignment ? programCellsByKey[annualCellKey] : undefined;
        if (pc && !isFixedSlot) {
          const progressTimetableText = (pc.timetable_text ?? '').trim();
          if (rawDisplayText && progressTimetableText && rawDisplayText !== progressTimetableText) {
            pc = undefined;
          }
        }
        const eventKeyForFixed = isFixedSlot
          ? normalizeFixedEventName(timetableMemoRaw) || normalizeFixedEventName(rawDisplayText)
          : '';
        const fixedMatchByCell = isFixedSlot ? fixedContentBySourceAnnualKey.get(annualCellKey.trim()) ?? null : null;
        const fixedMatchByEvent =
          isFixedSlot && eventKeyForFixed ? fixedContentByEventName.get(eventKeyForFixed) ?? null : null;
        const rowColorLower = displayColor.trim().toLowerCase();
        const projectForRow =
          programKindLabel === '프로' ? projectProgramByColor.get(rowColorLower) ?? null : null;
        const scopeLookupAlias = ((pc?.timetable_text ?? '').trim() || normalizedDisplayText.trim());
        const scopeLookupKey =
          templateLabel.trim() && scopeLookupAlias ? `${templateLabel.trim()}|${scopeLookupAlias}` : '';
        const trackModeForRow = templateLabel.trim()
          ? trackProgramModeByTrack[templateLabel.trim()] ?? 'unified'
          : 'unified';
        const preferredTrackScopeKey =
          !isFixedSlot && programKindLabel !== '프로' && templateLabel.trim()
            ? trackModeForRow === 'unified'
              ? `${templateLabel.trim()}|-`
              : trackModeForRow === 'sync'
                ? `${templateLabel.trim()}|${trackProgramSyncRepByTrack[templateLabel.trim()]?.trim() || scopeLookupAlias || '-'}`
                : scopeLookupKey
            : '';
        const scopedOccurrenceKey = (() => {
          if (isFixedSlot) return '';
          if (programKindLabel === '프로' && projectForRow?.id) return `project:${projectForRow.id}`;
          const trackKey = trackModeForRow === 'sync' ? scopeLookupKey : preferredTrackScopeKey || scopeLookupKey;
          return trackKey ? `track:${trackKey}` : '';
        })();
        const scopedOccurrenceIndex = scopedOccurrenceKey
          ? scopedOccurrenceCountByScopeKey.get(scopedOccurrenceKey) ?? 0
          : undefined;
        if (scopedOccurrenceKey) {
          scopedOccurrenceCountByScopeKey.set(scopedOccurrenceKey, (scopedOccurrenceIndex ?? 0) + 1);
        }
        const progressSource = resolveAnnualSlotProgressSource({
          scopedRowsIndex,
          annualCellKey,
          programKindLabel,
          isFixedSlot,
          hasTimetableAssignment,
          templateLabel,
          displayText: normalizedDisplayText,
          projectId: projectForRow?.id ?? null,
          legacyPc: pc,
          trackProgramModeByTrack,
          trackProgramSyncRepByTrack,
          scopedOccurrenceIndex,
          preferredTrackScopeKey,
          scopeLookupKey,
          fixedMatchByCell,
          fixedMatchByEvent,
        });
        const d = mapProgressLikeToDisplayFields(progressSource);
        const crossTopic = d.progressCross.trim();
        if (!crossTopic && !includeEmptyCross) continue;
        let contentRecordTarget: AnnualProgressProgramRecordTarget | null | undefined;
        if (includeEmptyCross) {
          const shouldPickScopedRow =
            !isFixedSlot &&
            hasTimetableAssignment &&
            (programKindLabel === '프로' ? !!projectForRow?.id : !!templateLabel.trim());
          const contentRow = shouldPickScopedRow
            ? pickScopedContentFromAnnualIndex(
                scopedRowsIndex,
                annualCellKey,
                programKindLabel,
                templateLabel,
                normalizedDisplayText,
                projectForRow?.id ?? null,
                pc?.timetable_text ?? undefined,
                scopedOccurrenceIndex,
                preferredTrackScopeKey
              )
            : null;
          const recordRow = shouldPickScopedRow
            ? pickScopedRecordRowFromAnnualIndex(
                scopedRowsIndex,
                annualCellKey,
                programKindLabel,
                templateLabel,
                normalizedDisplayText,
                projectForRow?.id ?? null,
                pc?.timetable_text ?? undefined,
                scopedOccurrenceIndex,
                scopeLookupKey,
                preferredTrackScopeKey,
                trackProgramModeByTrack
              )
            : null;
          const isMirrorSlot = isSyncTrackMirrorScope(
            templateLabel,
            scopeLookupKey,
            preferredTrackScopeKey,
            trackProgramModeByTrack
          );
          const repRow =
            isMirrorSlot && shouldPickScopedRow
              ? pickRepScopedContentRowForMirrorSlot(scopedRowsIndex, preferredTrackScopeKey, scopedOccurrenceIndex)
              : null;
          contentRecordTarget = resolveAnnualProgressRecordTargets({
            isFixedSlot,
            fixedRow: isFixedSlot ? fixedMatchByCell ?? fixedMatchByEvent : null,
            isMirror: isMirrorSlot,
            contentScopedRow: isMirrorSlot ? repRow ?? contentRow : contentRow,
            recordScopedRow: recordRow,
            aliasScopeKey: scopeLookupKey,
            programKindLabel,
            projectId: projectForRow?.id ?? null,
            scopedOccurrenceIndex,
            preferredTrackScopeKey,
            legacyPc: pc,
          }).evalTarget;
        }
        const isSyncTimetableMirror = resolveAnnualPresentationSyncMirror({
          programKindLabel,
          isFixedSlot,
          templateLabel,
          scopeLookupAlias,
          trackProgramModeByTrack,
          trackProgramSyncRepByTrack,
        });
        seq += 1;
        out.push({
          dateLabel: shortDateLabelForIso(dateIso),
          weekdayLabel: weekdayLabelForKey(weekdayKey),
          periodLabel: periodLabels[slotIndex] || `${slotIndex + 1}타임`,
          templateLabel: templateLabel.trim(),
          unit: d.progressUnit.trim(),
          learning: d.progressLearning.trim(),
          page: d.progressPage.trim(),
          assistantPage: d.progressAssistantPage.trim(),
          currentPeriod: d.progressCurrentPeriod.trim(),
          totalPeriod: d.progressTotalPeriod.trim(),
          material: d.progressMaterial.trim(),
          crossTopic,
          crossNote: d.progressCrossNote.trim(),
          isSyncTimetableMirror,
          annualCellKey,
          ...(includeEmptyCross ? { contentRecordTarget: contentRecordTarget ?? null } : {}),
          mondayIso: week.mondayIso,
          weekdayKey,
          slotIndex,
          seq,
        });
      }
    });
  });

  return out;
}

/** 연간진도표 테이블과 동일한 규칙으로 범교과 주제어(본문)만 수집 */
export function extractAnnualProgressCrossTopics(params: ExtractAnnualProgressCrossTopicsParams): string[] {
  return extractAnnualProgressCrossTopicDetailRows(params).map((row) => row.crossTopic);
}

export type CrossTopicDraftEntry = {
  annualCellKey: string;
  topic: string;
  /** 동기화 미러 슬롯 예측·표시용 */
  isSyncTimetableMirror?: boolean;
};

/**
 * 대표(또는 미러) 표에서 초안이 DB와 달라진 슬롯에 대해,
 * 동기화 형제 시간표 슬롯에도 같은 주제어를 예측 반영한다.
 */
export function expandCrossTopicDraftsForSyncPrediction(
  drafts: CrossTopicDraftEntry[],
  dbRows: AnnualCrossTopicDetailRow[],
  opts: {
    syncMirrorView: boolean;
    syncMirrorRepAnnualCellKeyByMirrorKey: Record<string, string>;
    syncMirrorMirrorAnnualCellKeysByRepKey: Record<string, string[]>;
  }
): CrossTopicDraftEntry[] {
  const dbTopicByKey = new Map<string, string>();
  for (const row of dbRows) {
    const key = (row.annualCellKey ?? '').trim();
    if (!key) continue;
    dbTopicByKey.set(key, (row.crossTopic ?? '').trim());
  }

  const out = new Map<string, CrossTopicDraftEntry>();
  for (const d of drafts) {
    const key = (d.annualCellKey ?? '').trim();
    if (!key) continue;
    const topic = (d.topic ?? '').trim();
    out.set(key, {
      annualCellKey: key,
      topic,
      isSyncTimetableMirror: opts.syncMirrorView,
    });

    const dbTopic = dbTopicByKey.get(key) ?? '';
    if (topic === dbTopic) continue;

    const repKey = opts.syncMirrorView
      ? (opts.syncMirrorRepAnnualCellKeyByMirrorKey[key]?.trim() || key)
      : key;
    const mirrorKeys = opts.syncMirrorMirrorAnnualCellKeysByRepKey[repKey] ?? [];

    if (opts.syncMirrorView && repKey && repKey !== key) {
      out.set(repKey, {
        annualCellKey: repKey,
        topic,
        isSyncTimetableMirror: false,
      });
    }
    for (const mk of mirrorKeys) {
      const mirrorKey = mk.trim();
      if (!mirrorKey || mirrorKey === key) continue;
      out.set(mirrorKey, {
        annualCellKey: mirrorKey,
        topic,
        isSyncTimetableMirror: true,
      });
    }
  }
  return Array.from(out.values());
}

/**
 * DB(연간진도표) 집계 행에 현재 편제 표 초안을 덮어쓴다.
 * - 초안에서 비우면 해당 슬롯은 집계에서 제외
 * - DB에 없던 슬롯에 초안 주제어가 있으면 추가
 */
export function mergeCrossTopicDraftsIntoDetailRows(
  dbRows: AnnualCrossTopicDetailRow[],
  drafts: CrossTopicDraftEntry[],
  options?: { draftIsSyncMirror?: boolean }
): AnnualCrossTopicDetailRow[] {
  const draftMap = new Map<string, CrossTopicDraftEntry>();
  for (const d of drafts) {
    const key = (d.annualCellKey ?? '').trim();
    if (!key) continue;
    draftMap.set(key, d);
  }
  if (draftMap.size === 0) return dbRows;

  const out: AnnualCrossTopicDetailRow[] = [];
  const appliedKeys = new Set<string>();

  for (const row of dbRows) {
    const key = (row.annualCellKey ?? '').trim();
    if (key && draftMap.has(key)) {
      appliedKeys.add(key);
      const draft = draftMap.get(key)!;
      const topic = (draft.topic ?? '').trim();
      if (!topic) continue;
      out.push({
        ...row,
        crossTopic: topic,
        isSyncTimetableMirror:
          typeof draft.isSyncTimetableMirror === 'boolean'
            ? draft.isSyncTimetableMirror
            : row.isSyncTimetableMirror,
      });
      continue;
    }
    out.push(row);
  }

  let seq = out.reduce((max, row) => Math.max(max, row.seq), 0);
  for (const [key, draft] of Array.from(draftMap.entries())) {
    const topic = (draft.topic ?? '').trim();
    if (!topic || appliedKeys.has(key)) continue;
    seq += 1;
    out.push({
      annualCellKey: key,
      dateLabel: '',
      weekdayLabel: '',
      periodLabel: '',
      templateLabel: '',
      unit: '',
      learning: '',
      page: '',
      assistantPage: '',
      currentPeriod: '',
      totalPeriod: '',
      material: '',
      crossTopic: topic,
      crossNote: '',
      isSyncTimetableMirror:
        typeof draft.isSyncTimetableMirror === 'boolean'
          ? draft.isSyncTimetableMirror
          : options?.draftIsSyncMirror === true,
      mondayIso: '',
      weekdayKey: 'mon',
      slotIndex: 0,
      seq,
    });
  }
  return out;
}
